/**
 * Instant trade: the user's P1–P3 presets (saved on the server) and the one-click actions used by the
 * floating panel. Orders go straight to the API
 * with no confirmation step; the user turns this on once in the settings (prefs.enabled).
 */
import { useEffect } from 'react';
import {
  clampLeverage,
  D,
  DEFAULT_INSTANT_PREFS,
  floorToStep,
  fmt,
  fmtUsd,
  instantMinOrder,
  instantSizes,
  leverageCap,
  pairLabel,
  qtyToLots,
  slPriceFromPct,
  tpPriceFromPct,
  tradeIdSuffix,
  usdPerPoint,
  type Instrument,
  type InstantPreset,
  type InstantSizeRules,
  type InstantSizing,
  type InstantTradePrefs,
  type Order,
  type Side,
  type Ticker,
} from '@platform/shared';
import { api, ApiError } from '../lib/api';
import { createStore, useStore } from '../lib/store';
import { marketStore } from './market';
import { sessionStore, useMe } from './session';
import { tradingStore } from './trading';

const PANEL_KEY = 'instantPanel';

interface PanelPos {
  x: number;
  y: number;
}

interface InstantState {
  prefs: InstantTradePrefs;
  /** Floating panel shown on the trade page. */
  open: boolean;
  pos: PanelPos | null;
  /** Last result, shown in the panel: ok = filled, otherwise the error. */
  notice: { ok: boolean; text: string; t: number } | null;
  /** Action key currently in flight (one at a time per button). */
  busy: string | null;
}

function readPanel(): { open: boolean; pos: PanelPos | null } {
  try {
    const v = JSON.parse(localStorage.getItem(PANEL_KEY) ?? '{}');
    const pos = v.pos && Number.isFinite(v.pos.x) && Number.isFinite(v.pos.y) ? { x: v.pos.x, y: v.pos.y } : null;
    return { open: v.open === true, pos };
  } catch {
    return { open: false, pos: null };
  }
}

export const instantStore = createStore<InstantState>({ prefs: DEFAULT_INSTANT_PREFS, ...readPanel(), notice: null, busy: null });

function savePanel() {
  const { open, pos } = instantStore.get();
  try {
    localStorage.setItem(PANEL_KEY, JSON.stringify({ open, pos }));
  } catch {
    /* storage unavailable: panel state lasts for this visit */
  }
}

export function setInstantOpen(open: boolean) {
  instantStore.set({ open });
  savePanel();
}

export function setInstantPos(pos: PanelPos) {
  instantStore.set({ pos });
  savePanel();
}

/** Load the signed-in user's presets; defaults when signed out. Mount once. */
export function useInstantSync() {
  const me = useMe();
  const id = me?.id ?? null;
  useEffect(() => {
    instantStore.set({ prefs: DEFAULT_INSTANT_PREFS });
    if (!id) return;
    api
      .get<InstantTradePrefs>('/api/instant-trade')
      .then((prefs) => instantStore.set({ prefs }))
      .catch(() => {
        /* keep defaults; the panel still works with them */
      });
  }, [id]);
}

export async function saveInstantPrefs(prefs: InstantTradePrefs): Promise<InstantTradePrefs> {
  const saved = await api.put<InstantTradePrefs>('/api/instant-trade', prefs);
  instantStore.set({ prefs: saved });
  return saved;
}

export const useInstantPrefs = () => useStore(instantStore, (s) => s.prefs);
export const useInstant = () => useStore(instantStore, (s) => s);
export const activePreset = (p: InstantTradePrefs): InstantPreset => p.presets[p.active];

// ---------- actions ----------

function activeAccount() {
  const s = sessionStore.get();
  return s.me?.accounts.find((a) => a.kind === s.kind) ?? null;
}

/** Why one-click trading can't run on this symbol right now, or null. */
export function instantBlocker(symbol: string): string | null {
  const s = sessionStore.get();
  if (!s.me) return 'Log in to trade';
  const acc = activeAccount();
  if (!acc) return 'No trading account';
  if (!instantStore.get().prefs.enabled) return 'Turn on instant trade in its settings first';
  const t = marketStore.get().tickers[symbol];
  if (!t) return 'No price yet';
  if (!t.open) return 'Market is closed';
  if (t.paused && acc.kind === 'live') return 'Live price unavailable, trading is paused';
  return null;
}

async function run(key: string, fn: () => Promise<string>) {
  if (instantStore.get().busy) return;
  instantStore.set({ busy: key });
  try {
    const text = await fn();
    instantStore.set({ notice: { ok: true, text, t: Date.now() } });
  } catch (e) {
    instantStore.set({ notice: { ok: false, text: e instanceof ApiError || e instanceof Error ? e.message : 'Order failed', t: Date.now() } });
  } finally {
    instantStore.set({ busy: null });
  }
}

const sideWord = (s: Side) => (s === 'long' ? 'Long' : 'Short');

/** The instrument's size limits, one lot valued at the price a `side` order opens at; null without a price. */
function sizeRules(inst: Instrument, t: Ticker, side: Side): InstantSizeRules | null {
  const lotValue = D.from(side === 'long' ? t.ask : t.bid).mul(inst.lotSize).mul(usdPerPoint(inst.contractSize, t.fx));
  if (!lotValue.isPos()) return null;
  return { lotValue, lotStep: inst.lotStep, minLots: inst.minLots, minNotional: inst.minNotional, maxNotional: inst.maxNotional };
}

/**
 * What each of the preset's amounts opens on the instrument at this price (INSTANT_MIN_VOLUME_PLAN.md):
 * the panel draws its buttons from it and a click sends the same lots, so no button is too small.
 */
export function instantSizing(inst: Instrument, t: Ticker, side: Side, amounts: readonly string[]): InstantSizing | null {
  const r = sizeRules(inst, t, side);
  return r && instantSizes(amounts, r);
}

/** The smallest order the instrument takes for a `side` order at this price. */
export function instantMinFor(inst: Instrument, t: Ticker, side: Side) {
  const r = sizeRules(inst, t, side);
  return r && instantMinOrder(r);
}

/** The API's size refusals, should one still come back, in the panel's words. */
function sizeRefusal(e: unknown, inst: Instrument, t: Ticker, side: Side): unknown {
  const r = sizeRules(inst, t, side);
  if (!r || !(e instanceof ApiError) || (e.code !== 'below_min_lots' && e.code !== 'below_min')) return e;
  const min = instantMinOrder(r);
  return new Error(`Too small for ${pairLabel(inst.symbol)}: its smallest order is ${min.lots.toFixed(min.lotDp)} lots (≈ ${fmtUsd(min.value)})`);
}

const leverageFor = (inst: Instrument, p: InstantPreset) =>
  clampLeverage(p.leverage, { minLeverage: inst.minLeverage, maxLeverage: leverageCap(inst.maxLeverage, sessionStore.get().me?.maxLeverage) });

/** Market order for the active preset's `index`-th size, with its leverage and TP/SL, sent in lots. */
export function instantOpen(symbol: string, side: Side, index: number) {
  return run(`${side}:${index}`, async () => {
    const block = instantBlocker(symbol);
    if (block) throw new Error(block);
    const inst = marketStore.get().bySymbol[symbol];
    const t = marketStore.get().tickers[symbol];
    if (!inst || !t) throw new Error('No price yet');
    const p = activePreset(instantStore.get().prefs);
    // Worked out again from the latest price, so the lots sent are the button's, never under the minimum.
    const sizing = instantSizing(inst, t, side, p.amounts);
    const size = sizing?.sizes[index];
    if (!sizing || !size) throw new Error('No price yet');
    if (size.overMax) throw new Error(`Above ${pairLabel(symbol)}'s max order of ${fmtUsd(inst.maxNotional)}`);
    const ref = side === 'long' ? t.ask : t.bid;
    const tp = p.tpPct ? tpPriceFromPct(side, ref, p.tpPct).round(inst.priceDecimals) : null;
    const sl = p.slPct ? slPriceFromPct(side, ref, p.slPct).round(inst.priceDecimals) : null;
    const res = await api
      .post<{ order: Order }>(
        '/api/orders',
        {
          accountId: activeAccount()!.id,
          symbol,
          side,
          type: 'market',
          lots: size.lots.toString(),
          leverage: leverageFor(inst, p),
          tpPrice: tp?.toString(),
          slPrice: sl?.toString(),
          channel: 'instant',
        },
        { idempotencyKey: crypto.randomUUID() },
      )
      .catch((e: unknown) => {
        throw sizeRefusal(e, inst, t, side);
      });
    return `${sideWord(side)} ${size.lots.toFixed(sizing.lotDp)} lots ${pairLabel(symbol)} (${fmtUsd(res.order.notional)}) · filled ${fmt(res.order.fillPrice, inst.priceDecimals)}${tradeIdSuffix(res.order.tradeId)}`;
  });
}

/** Close `fraction` (0–1) of every open position on the symbol in the active account. */
export function instantClose(symbol: string, fraction: string) {
  return run(`close:${fraction}`, async () => {
    const acc = activeAccount();
    if (!acc) throw new Error('No trading account');
    if (!tradingStore.get().positions.some((p) => p.symbol === symbol)) throw new Error(`No open ${pairLabel(symbol)} position`);
    const r = await api.post<{ closed: number }>('/api/positions/close-all', { accountId: acc.id, symbol, fraction, channel: 'instant' });
    const share = D.from(fraction).mul(100).toFixed(0);
    return `Closed ${share}% of ${r.closed} ${pairLabel(symbol)} position${r.closed === 1 ? '' : 's'}`;
  });
}

/** Net position size on the symbol: + long / − short. */
export function netPosition(symbol: string): D {
  let qty = D.ZERO;
  for (const p of tradingStore.get().positions) if (p.symbol === symbol) qty = p.side === 'long' ? qty.add(p.qty) : qty.sub(p.qty);
  return qty;
}

/**
 * Close everything on the symbol and open the same volume the other way. It is sent in lots: an amount
 * in USD bought at the other side of the spread would round down a lot step (a 0.01-lot short to 0 lots).
 */
export function instantFlip(symbol: string) {
  return run('flip', async () => {
    const block = instantBlocker(symbol);
    if (block) throw new Error(block);
    const acc = activeAccount()!;
    const net = netPosition(symbol);
    if (net.isZero()) throw new Error(`No open ${pairLabel(symbol)} position to flip`);
    const side: Side = net.isPos() ? 'short' : 'long';
    const inst = marketStore.get().bySymbol[symbol]!;
    const t = marketStore.get().tickers[symbol]!;
    const r = sizeRules(inst, t, side);
    if (!r) throw new Error('No price yet');
    // Checked before the close: a reopen the API would refuse must not leave the client flat.
    const min = instantMinOrder(r);
    const lots = floorToStep(qtyToLots(net.abs(), inst.lotSize), inst.lotStep);
    if (lots.lt(min.lots)) throw new Error(`Flip opens at least ${min.lots.toFixed(min.lotDp)} lots on ${pairLabel(symbol)}; this position is smaller. Close it instead.`);
    const value = lots.mul(r.lotValue);
    if (value.gt(inst.maxNotional)) throw new Error(`Flip would open ${fmtUsd(value)}, above ${pairLabel(symbol)}'s max order of ${fmtUsd(inst.maxNotional)}`);
    await api.post('/api/positions/close-all', { accountId: acc.id, symbol, channel: 'instant' });
    const p = activePreset(instantStore.get().prefs);
    const res = await api.post<{ order: Order }>(
      '/api/orders',
      { accountId: acc.id, symbol, side, type: 'market', lots: lots.toString(), leverage: leverageFor(inst, p), channel: 'instant' },
      { idempotencyKey: crypto.randomUUID() },
    );
    return `Flipped to ${side} ${lots.toFixed(min.lotDp)} lots (${fmtUsd(res.order.notional)}) · filled ${fmt(res.order.fillPrice, inst.priceDecimals)}${tradeIdSuffix(res.order.tradeId)}`;
  });
}
